import { Skeleton } from "antd"
import { useCompletedWorkareaVisitsQuery } from "../../domain/invoicing/invoicingQueries"
import ApprovedPaymentSummaries from "./ApprovedPaymentSummaries/ApprovedPaymentSummaries"
import { ItemsContext } from "../Items/ItemsRoot"
import { useContext, useEffect, useState } from "react"
import { getQboLinkingWarnings } from "./invoiceSummaryModel"
import { useWorkareaGroupsQuery } from "../../domain/workareas/groupsQueries"
import { PricingTypes } from "../../common/constants"
import { useCostCodes, useCostCodesInvalidator } from "../../domain/costCodes/costCodeQueries"
import QuickBooksLinkingWarnings from "./QuickBooksLinkingWarnings"
import InvoiceListTable from "./InvoiceList/InvoiceListTable"
import InvoiceTotals from "./Totals/InvoiceTotals"
import CreateInvoiceButtonAndModal from "./Creation/CreateInvoiceButtonAndModal"
import { useInvoicingSettingsQuery } from "../../domain/invoicing/settings/invoicingSettingsQueries"
import { defaultInvoicingSettings } from "../../domain/invoicing/settings/invoicingSettings"
import { AuthenticationContext } from "../../AuthenticationContext"
import JobInvoicingContext, { ActiveInvoicingModal } from "./JobInvoicingContext"
import { useCustomerEmailsQuery } from "../../domain/customers/customerQueries"
import SendInvoiceModal from "./SendInvoice/SendInvoiceModal"
import { getBestCustomerEmail } from "../../domain/customers/customer"
import PayInvoiceModal from "./PayInvoice/PayInvoiceModal"
import SynkedUPPaymentsBanner from './SynkedUPPaymentsBanner'
import SectionCard from "../common/SectionCard"

type Props = {
  jobNeedsToBeSaved: boolean
  customerId: number
  customerQboId: string | null | undefined
  onLinkCustomerToQbo?: () => void
  onLinkCostCodeToQbo?: (costCodeId: number) => void
  costCodeRefreshCounter: number
  onInvoicesChanged: () => void
}

const JobInvoicing = ({ jobNeedsToBeSaved, customerId, customerQboId, onLinkCustomerToQbo, onLinkCostCodeToQbo, costCodeRefreshCounter, onInvoicesChanged }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { jobData, workareaSummaries } = useContext(ItemsContext)
  const workareaGroupsQuery = useWorkareaGroupsQuery(jobData.jobId)
  const completedWorkareaVisitsQuery = useCompletedWorkareaVisitsQuery(jobData.jobId, jobData.workareas.some(x => x.pricingType === PricingTypes.PerVisit))
  const costCodesQuery = useCostCodes()
  const costCodesInvalidator = useCostCodesInvalidator()
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const invoicingSettings = invoicingSettingsQuery.data ?? defaultInvoicingSettings()
  const [activeInvoicingModal, setActiveInvoicingModal] = useState<ActiveInvoicingModal | null>(null)
  const customerEmailsQuery = useCustomerEmailsQuery(customerId)

  useEffect(() => {
    costCodesInvalidator.invalidate()
  }, [costCodeRefreshCounter])

  const isAnythingLoading = workareaGroupsQuery.isLoading
    || completedWorkareaVisitsQuery.isLoading
    || costCodesQuery.isLoading
    || invoicingSettingsQuery.isLoading

  if (isAnythingLoading || jobData.jobId === 0) {
    return <section>
      <Skeleton active />
    </section>
  }

  const qboLinkingWarnings = getQboLinkingWarnings({
    invoicingSettings,
    isTenantConnectedToQuickBooks: currentUser?.isTenantConnectedToQuickBooks ?? false,
    customerQboId,
    workareas: workareaGroupsQuery.data?.flatMap(x => x.workareas) ?? [],
    costCodes: costCodesQuery.data ?? []
  })

  return <JobInvoicingContext.Provider value={{
    activeInvoicingModal,
    setActiveInvoicingModal,
    customerEmails: customerEmailsQuery.data ?? []
  }}>
    <SynkedUPPaymentsBanner />
    <InvoiceTotals
      jobId={jobData.jobId}
      workareaGroups={workareaGroupsQuery.data ?? []}
      workareaSummaries={workareaSummaries}
      completedWorkareaVisits={completedWorkareaVisitsQuery.data ?? []}
    />
    <ApprovedPaymentSummaries
      jobId={jobData.jobId}
    />
    <SectionCard title="Invoices">
      <InvoiceListTable
        jobId={jobData.jobId}
        preventExports={!customerQboId}
      />
      <QuickBooksLinkingWarnings
        warnings={qboLinkingWarnings}
        costCodes={costCodesQuery.data ?? []}
        onLinkCustomerToQbo={onLinkCustomerToQbo}
        onLinkCostCodeToQbo={onLinkCostCodeToQbo}
      />
      <div style={{textAlign: 'right'}}>
        <CreateInvoiceButtonAndModal
          jobNeedsToBeSaved={jobNeedsToBeSaved}
          onInvoiceCreated={onInvoicesChanged}
          jobId={jobData.jobId}
          workareaGroups={workareaGroupsQuery.data ?? []}
          workareaSummaries={workareaSummaries}
          completedWorkareaVisits={completedWorkareaVisitsQuery.data ?? []}
          hasQboLinkingWarnings={qboLinkingWarnings.length > 0}
          invoicingSettings={invoicingSettings}
        />
      </div>
    </SectionCard>
    <SendInvoiceModal
      isOpen={activeInvoicingModal?.modal === 'sendInvoice'}
      onClose={() => setActiveInvoicingModal(null)}
      invoiceId={activeInvoicingModal?.invoiceId ?? ''}
      emailAddresses={[getBestCustomerEmail(customerEmailsQuery.data ?? []) ?? '']}
      invoicingSettings={invoicingSettings}
    />
    <PayInvoiceModal
      isOpen={activeInvoicingModal?.modal === 'payInvoice'}
      onClose={() => setActiveInvoicingModal(null)}
      invoiceId={activeInvoicingModal?.invoiceId ?? ''}
      customerId={customerId}
    />
  </JobInvoicingContext.Provider>
}

export default JobInvoicing
